import { defineStore } from "pinia";
import axios from "@/app/plugins/axios";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { useProductsStore } from "./products";
import { useAccountStore } from "@/account/stores/account";
import i18n from "@/app/plugins/i18n";
import router from "@/app/router";
import {
  ProductClass,
  ProductClassesStore,
  ProductClassFilterCondition,
} from "@/catalog/types/productClass";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import default_product_image from "@/shared/assets/default_product_image.png";

export type ProductClassPayloadValue =
  | string
  | number
  | boolean
  | null
  | string[]
  | Record<string, string>;
export type ProductClassPayload = Record<string, ProductClassPayloadValue>;

export interface ProductClassSearchPagination {
  limit?: number;
  nextPage?: string;
  prevPage?: string;
  page?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface ProductClassSearchOptions {
  pagination?: ProductClassSearchPagination;
  filters?: ProductClassFilterCondition;
  includeStoredFilters?: boolean;
  updateStore?: boolean;
}

export interface ProductClassSearchResponse {
  data: ProductClass[];
  hasNext?: boolean;
  hasPrev?: boolean;
}

export const useProductClassesStore = defineStore("productClass", {
  state: (): ProductClassesStore => ({
    productClasses: [],
    selectedProductClass: null,
    loadedAccountId: "",
    loading: false,
    filterProductClassCondition: {},
    listLoading: false,
    noResults: false,
    hasMore: false,
    hasNext: false,
    hasPrev: false,
    nextPage: "",
  }),

  getters: {
    isLoading: (state) => state.loading,
    isListLoading: (state) => state.listLoading,
    getProductClasses: (state) => state.productClasses,
    getSelectedProductClass: (state) => state.selectedProductClass,
  },

  actions: {
    setSelectedProductClass(productClass: ProductClass): void {
      this.selectedProductClass = productClass;
    },

    setFilterProductClassCondition(condition: ProductClassFilterCondition): void {
      this.filterProductClassCondition = condition;
    },

    setSelectedProductClassNull(): void {
      this.selectedProductClass = null;
    },

    ensureCurrentAccountCache(accountId: string): void {
      if (this.loadedAccountId && this.loadedAccountId !== accountId) {
        this.productClasses = [];
        this.selectedProductClass = null;
        this.filterProductClassCondition = {};
        this.noResults = false;
        this.hasMore = false;
        this.hasNext = false;
        this.hasPrev = false;
        this.nextPage = "";
        this.loadedAccountId = "";
      }
    },

    getProductClassDefaultImage(images?: string[]): string {
      return images?.find((image) => image.includes("default.jpg")) || default_product_image;
    },
    // Helper function to filter out empty values from payload
    _filterEmptyValues(data: ProductClassPayload): ProductClassPayload {
      const filteredData: ProductClassPayload = {};
      const keepEmptyArrayKeys = new Set(["tags"]);

      for (const [key, value] of Object.entries(data)) {
        // Skip null, undefined, empty strings
        if (value === null || value === undefined || value === "") {
          continue;
        }

        // Handle empty arrays
        if (Array.isArray(value) && value.length === 0 && !keepEmptyArrayKeys.has(key)) {
          continue;
        }

        // Handle empty objects
        if (
          typeof value === "object" &&
          !Array.isArray(value) &&
          value !== null &&
          Object.keys(value).length === 0
        ) {
          continue;
        }

        filteredData[key] = value;
      }

      return filteredData;
    },

    async createProductClass(productClassData: ProductClassPayload) {
      try {
        this.loading = true;
        // Filter out empty values before sending the request
        const filteredData = this._filterEmptyValues(productClassData);
        const response = await axios.post<ProductClass>("/productClass", filteredData);
        const productClass = response.data;
        const accountId = useAccountStore().account?.id;
        if (accountId) {
          const uploadedImages = await useProductsStore().uploadImages(
            productClass?.id,
            accountId,
            true,
          );
          await axios.patch(`/productClass/${productClass?.id}`, {
            images: useProductsStore().filterImagesPayload(uploadedImages),
          });
        }
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productClassDetails.productClassCreated"),
        });
        router.push({ path: `/product-class/${productClass.id}` });
        return response;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        this.loading = false;
      }
    },

    async retrieveProductClass(id: string | string[] | number, doLoading = true): Promise<void> {
      try {
        if (doLoading) this.loading = true;
        const response = await axios.get<ProductClass>(`/productClass/${id}`);
        const productClass = response.data;
        this.selectedProductClass = productClass;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      } finally {
        if (doLoading) this.loading = false;
      }
    },

    async updateProductClass(updateData: ProductClassPayload, id: string | string[] | number) {
      try {
        // Filter out empty values before sending the request
        const filteredData = this._filterEmptyValues(updateData);
        const response = await axios.patch<ProductClass>(`/productClass/${id}`, filteredData);

        const productClass = response.data;

        const productClassIndex = this.productClasses?.findIndex(
          (p: ProductClass) => p.id === productClass.id,
        );
        if (productClassIndex !== -1) {
          this.productClasses[productClassIndex] = productClass;
        }

        this.selectedProductClass = response.data;

        const accountId = useAccountStore().account?.id;
        if (accountId) {
          const uploadedImages = await useProductsStore().uploadImages(
            productClass?.id,
            accountId,
            true,
          );
          await axios.patch(`/productClass/${productClass.id}`, {
            images: useProductsStore().filterImagesPayload(uploadedImages),
          });
        }

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productClassDetails.productClassUpdated"),
        });
        router.push({ path: `/product-class/${productClass.id}` });
        return response;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
      }
    },

    async searchProductClasses(
      {
        pagination = {},
        filters = {},
        includeStoredFilters = true,
        updateStore = true,
      }: ProductClassSearchOptions = {},
      initLoading = false,
    ) {
      if (initLoading) this.loading = true;
      else {
        this.listLoading = true;
        if (updateStore) {
          this.productClasses = [];
        }
      }

      try {
        const currentAccountId = useAccountStore().account?.id || "";
        const payload = {
          filters: {
            ...(includeStoredFilters ? this.filterProductClassCondition : {}),
            ...filters,
          },
          pagination: {
            limit: 10,
            ...pagination,
          },
        };

        const response = await axios.post<ProductClassSearchResponse>(
          "/productClass/search",
          payload,
        );
        const result = response.data;

        const productClasses = result.data || [];

        if (updateStore) {
          this.hasNext = false;
          this.hasPrev = false;

          // Check if there's a next page - use hasNext from the response
          this.hasNext = result.hasNext || false;

          // Check if there's a previous page - use hasPrev from the response
          this.hasPrev = result.hasPrev || false;

          this.noResults = productClasses.length === 0;
          this.productClasses = productClasses;
          this.loadedAccountId = currentAccountId;
        }

        return productClasses;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return [];
      } finally {
        this.loading = false;
        this.listLoading = false;
      }
    },

    async checkIfProductClassesExist(): Promise<boolean> {
      try {
        const response = await axios.post<ProductClassSearchResponse>("/productClass/search", {
          filters: {},
          pagination: {
            limit: 1,
          },
        });
        return response.data.data.length > 0;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return false;
      }
    },

    async exportProductClasses(customFilters = {}): Promise<boolean> {
      try {
        useSnackBarStore().toastInfo({
          message:
            i18n.global.t("common.action.preparingExport") || "Preparing export, please wait...",
        });

        const payload = {
          filters: {
            ...this.filterProductClassCondition,
            ...customFilters,
          },
        };

        const response = await axios.post("/productClass/export", payload, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let filename = "product-groups-export.csv";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        useSnackBarStore().toastSuccess({
          message:
            i18n.global.t("common.action.exportProductClassesSuccess") ||
            "Product groups export completed successfully",
        });

        return true;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getErrorMessage(error, "Failed to export product groups data"),
        });
        return false;
      }
    },

    async removeFromAllProducts(id: string): Promise<boolean> {
      try {
        this.loading = true;
        await axios.post(`/productClass/${id}/removeFromProducts`);

        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.productClassDetails.productClassRemovedFromAllProducts"),
        });

        return true;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
});
